/**
 * Manufacturer Hooks
 * ==================
 * React Query hooks for manufacturer operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { manufacturerApi } from '@/services/api'
import type { ManufacturerCreate, ManufacturerUpdate } from '@/types'

// Query keys
export const manufacturerKeys = {
  all: ['manufacturers'] as const,
  lists: () => [...manufacturerKeys.all, 'list'] as const,
  list: (includeCounts?: boolean) => [...manufacturerKeys.lists(), { includeCounts }] as const,
  details: () => [...manufacturerKeys.all, 'detail'] as const,
  detail: (id: number) => [...manufacturerKeys.details(), id] as const,
}

/**
 * Hook to fetch all manufacturers.
 */
export function useManufacturers(includeCounts = false) {
  return useQuery({
    queryKey: manufacturerKeys.list(includeCounts),
    queryFn: () => manufacturerApi.list(includeCounts),
  })
}

/**
 * Hook to fetch a single manufacturer.
 */
export function useManufacturer(id: number) {
  return useQuery({
    queryKey: manufacturerKeys.detail(id),
    queryFn: () => manufacturerApi.get(id),
    enabled: !!id,
  })
}

/**
 * Hook to create a new manufacturer.
 */
export function useCreateManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ManufacturerCreate) => manufacturerApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manufacturerKeys.lists() })
    },
  })
}

/**
 * Hook to update a manufacturer.
 */
export function useUpdateManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ManufacturerUpdate }) =>
      manufacturerApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: manufacturerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: manufacturerKeys.detail(id) })
    },
  })
}

/**
 * Hook to delete a manufacturer.
 */
export function useDeleteManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reassignTo }: { id: number; reassignTo?: number }) =>
      manufacturerApi.delete(id, reassignTo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: manufacturerKeys.lists() })
    },
  })
}
